if (!customElements.get('product-custom-options')) {
  customElements.define(
    'product-custom-options',
    class ProductCustomOptions extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.productFormId);
        this.lengthNote = this.querySelector('[data-custom-length-note]');
        this.colorNote = this.querySelector('[data-custom-color-note]');
        this.lengthNoteInput = this.lengthNote?.querySelector('textarea');
        this.colorNoteInput = this.colorNote?.querySelector('textarea');
        this.errorEl = this.querySelector('[data-custom-options-error]');
        this.lengthRadios = this.querySelectorAll('[name="properties[Hair Length]"]');
        this.colorRadios = this.querySelectorAll('[data-theme-color]');
        this.hairLengthRequired = this.dataset.hairLengthRequired === 'true';
        this.colorRequired = this.dataset.colorRequired === 'true';
        this.hasThemeColors = this.dataset.hasThemeColors === 'true';
        this.colorOptionName = (this.dataset.colorOptionName || 'Color').toLowerCase();
        this.customColorMatchers = (this.dataset.customColorMatch || 'Color Mix,Custom Color Mix,Custom')
          .split(',')
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean);

        this.onLengthChange = this.onLengthChange.bind(this);
        this.onThemeColorChange = this.onThemeColorChange.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.updateColorNote = this.updateColorNote.bind(this);

        this.lengthRadios.forEach((radio) => radio.addEventListener('change', this.onLengthChange));
        this.colorRadios.forEach((radio) => radio.addEventListener('change', this.onThemeColorChange));

        const productInfo = this.closest('product-info') || document;
        productInfo.addEventListener('change', this.onVariantChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.updateColorNote);
        }

        if (this.form) {
          this.form.addEventListener('submit', this.onSubmit, true);
        }

        this.onLengthChange();
        this.updateSelectedColorLabel();
        this.updateColorNote();
      }

      disconnectedCallback() {
        this.lengthRadios.forEach((radio) => radio.removeEventListener('change', this.onLengthChange));
        this.colorRadios.forEach((radio) => radio.removeEventListener('change', this.onThemeColorChange));
        const productInfo = this.closest('product-info') || document;
        productInfo.removeEventListener('change', this.onVariantChange);
        this.variantChangeUnsubscriber?.();
        if (this.form) {
          this.form.removeEventListener('submit', this.onSubmit, true);
        }
      }

      onLengthChange() {
        const selected = this.querySelector('[name="properties[Hair Length]"]:checked');
        const isCustom = Boolean(selected?.hasAttribute('data-custom-length-trigger'));
        this.toggleNote(this.lengthNote, this.lengthNoteInput, isCustom);
        this.clearError();
      }

      onThemeColorChange() {
        this.updateSelectedColorLabel();
        this.updateColorNote();
      }

      updateSelectedColorLabel() {
        const label = this.querySelector('[data-theme-color-selected]');
        if (!label) return;
        const selected = this.querySelector('[data-theme-color]:checked');
        label.textContent = selected ? selected.value : '';
      }

      onVariantChange(event) {
        const target = event.target;
        if (!target) return;
        if (target.hasAttribute('data-theme-color') || target.name === 'properties[Hair Length]') return;
        this.updateColorNote();
      }

      updateColorNote() {
        if (!this.colorNote) return;

        const selectedColor = this.getSelectedColorValue();
        const isCustom = this.isCustomColor(selectedColor);
        this.toggleNote(this.colorNote, this.colorNoteInput, isCustom);
        this.clearError();
      }

      getSelectedColorValue() {
        const themeColor = this.querySelector('[data-theme-color]:checked');
        if (themeColor) return themeColor.value || '';

        const variantSelects = document.getElementById(`variant-selects-${this.dataset.section}`);
        if (!variantSelects) return '';

        const fieldsets = variantSelects.querySelectorAll('fieldset');
        for (const fieldset of fieldsets) {
          const legend = fieldset.querySelector('legend');
          const label = (legend?.textContent || '').replace(':', '').trim().toLowerCase();
          if (!label.startsWith(this.colorOptionName)) continue;
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          return checked?.value || '';
        }

        const selects = variantSelects.querySelectorAll('select');
        for (const select of selects) {
          const name = (select.name || '').toLowerCase();
          if (name.includes(`[${this.colorOptionName}]`) || name.includes(this.colorOptionName)) {
            return select.value || '';
          }
        }

        return '';
      }

      isCustomColor(value) {
        if (!value) return false;
        const normalized = value.toLowerCase();
        return this.customColorMatchers.some((matcher) => normalized.includes(matcher));
      }

      toggleNote(wrapper, input, show) {
        if (!wrapper || !input) return;

        wrapper.hidden = !show;
        input.disabled = !show;

        if (!show) {
          input.value = '';
          input.removeAttribute('required');
        } else {
          input.setAttribute('required', 'required');
        }
      }

      onSubmit(event) {
        const error = this.validate();
        if (!error) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError(error);
      }

      validate() {
        if (this.hasThemeColors && this.colorRequired && this.colorRadios.length) {
          const selected = this.querySelector('[data-theme-color]:checked');
          if (!selected) {
            return 'Please select a color.';
          }
        }

        if (this.hairLengthRequired && this.lengthRadios.length) {
          const selected = this.querySelector('[name="properties[Hair Length]"]:checked');
          if (!selected) {
            return 'Please select a hair length.';
          }
        }

        if (this.lengthNote && !this.lengthNote.hidden) {
          const value = this.lengthNoteInput?.value?.trim();
          if (!value) {
            this.lengthNoteInput?.focus();
            return 'Please enter your custom length.';
          }
        }

        if (this.colorNote && !this.colorNote.hidden) {
          const value = this.colorNoteInput?.value?.trim();
          if (!value) {
            this.colorNoteInput?.focus();
            return 'Please describe your custom color.';
          }
        }

        return null;
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.hidden = false;
        this.errorEl.textContent = message;
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }
    }
  );
}
